'use strict';
angular.module('kerp-forms.forms').factory('FCA_SCHEMA', [
  'fieldDefinitionService', 'kirkleesCareAssessmentConstants', 'dateUtils', 'htmlService', 'kirkleesCareAssessmentService',
  function (fieldDefinitionService, constants, dateUtils, htmlService, kirkleesCareAssessmentService) {

    var attendanceAllowances = constants.attendanceAllowance;
    var mobilityAllowances = constants.mobilityAllowance;
  
    function toTitleMapEntry(level, amount) {
      return {value: level, name: amount.toFixed(2)};
    }

    function titleMap(titlesObj) {
      var titlesList = [];

      angular.forEach(titlesObj, function (label, value) {
        titlesList.push({value: value, name: label});
      });
      return titlesList;
    }

    function careHomeWorkingFieldOverrides() {
      return {
        personalLivingAllowance: {
          type: 'string',
          readOnly: true,
          title: 'Personal Expenses Allowance',
          'x-schema-form': {
            description: 'This is the weekly amount the government says you need for personal expenses when in a care home'
          }
        }
      };
    }

    var addressField = fieldDefinitionService.address;

    function address(options) {
      return angular.extend({
        type: 'object',
        title: 'Address',
        properties: {
          uprn: {
            type: 'string'
          },
          line1: addressField.requiredLine(null, true),
          line2: addressField.requiredLine(null, true),
          line3: addressField.optionalLine(null, true),
          postcode: addressField.postcode()
        },
        required: ['line1', 'line2', 'postcode']
      }, options);
    }

    function frequency(options) {
      return angular.extend({
        type: 'string',
        title: 'Frequency',
        "enum": ["Weekly", "Fortnightly", "4 Weekly", "Monthly", "Annually"]
      }, options);
    }

    function customFrequency(options) {
      return frequency({
        enum: options,
      });
    }

    function income(fieldDescriptions) {
      fieldDescriptions = fieldDescriptions || {};

      return {
        type: 'object',
        title: 'Income',
        properties: {
          hasUniversalCredit: fieldDefinitionService.boolean.yesNoSchema(),
          universalCredit: {
            type: 'object',
            properties: {
              standardAllowance: fieldDefinitionService.number.currencyField({
                title: 'Standard allowance'
              }),
              housingElement: fieldDefinitionService.number.currencyField({
                title: 'Housing element'
              }),
              children: fieldDefinitionService.number.currencyField({
                title: 'Children'
              }),
              disabledChild: fieldDefinitionService.number.currencyField({
                title: 'Disabled children'
              }),
              childcare: fieldDefinitionService.number.currencyField({
                title: 'Childcare'
              }),
              carer: fieldDefinitionService.number.currencyField({
                title: 'Carer'
              }),
              limitedWorkCapability: fieldDefinitionService.number.currencyField({
                title: 'Limited capability for work'
              }),
              limitedWorkAndRelatedCapability: fieldDefinitionService.number.currencyField({
                title: 'Limited capability for work and work-related activity'
              }),
              other: fieldDefinitionService.number.currencyField({
                title: 'Total taken off for other benefits'
              }),
              earnings: fieldDefinitionService.number.currencyField({
                title: 'Total taken off for take home pay'
              }),
              capitalTariff: fieldDefinitionService.number.currencyField({
                title: 'Total taken off for savings and capital'
              }),
              benefitLimit: fieldDefinitionService.number.currencyField({
                title: 'Total taken off for benefit cap limit'
              }),
              advanceRepayments: fieldDefinitionService.number.currencyField({
                title: 'Total taken off for advance repayments'
              }),
              debts: fieldDefinitionService.number.currencyField({
                title: 'Total taken off for debt and loan repayments'
              }),
              landlordPayment: fieldDefinitionService.number.currencyField({
                title: 'Total taken off for payment to landlord'
              })
            },
            required: [
              'standardAllowance',
              'housingElement',
              'children',
              'disabledChild',
              'childcare',
              'carer',
              'limitedWorkCapability',
              'limitedWorkAndRelatedCapability',
              'other',
              'earnings',
              'capitalTariff',
              'benefitLimit',
              'advanceRepayments',
              'debts',
              'landlordPayment'
            ]
          },

          hasWage: fieldDefinitionService.boolean.yesNoSchema(),

          wages: {
            type: 'array',
            minItems: 1,
            items: {
              type: 'object',
              properties: {
                from: {
                  title: 'Employer',
                  type: 'string'
                },
                ref: {
                  title: 'Payroll Ref. No.',
                  type: 'string'
                },
                amount: fieldDefinitionService.number.currencyField(
                  {title: 'Amount (net)'},
                  {description: fieldDescriptions.salaryAmount || 'This is the amount you receive after deductions and taxes have been taken off. You will find this figure on your wage slip or pay cheque. If you are paid directly into a bank account, this figure will be shown on your bank statement.'}
                ),
                amountPeriod: frequency()
              },
              required: ["from", "amount", "amountPeriod"]
            }
          },

          hasPrivatePension: fieldDefinitionService.boolean.yesNoSchema(),

          privatePensions: {
            type: 'array',
            minItems: 1,
            items: {
              type: "object",
              properties: {
                from: {
                  title: 'Company',
                  type: 'string'
                },
                ref: {
                  title: 'Ref. No.',
                  type: 'string'
                },
                amount: fieldDefinitionService.number.currencyField(
                  {title: 'Amount (net)'},
                  {description: fieldDescriptions.pensionAmount || 'This is the amount you receive after deductions and taxes have been taken off. You will find this figure on your pension statement or cheque. If your pension is paid directly into a bank account, this figure will be shown on your bank statement.'}
                ),
                amountPeriod: frequency()
              },
              required: ["from", "amount", "amountPeriod"]
            }
          },

          isEntitledToCarersPremium: fieldDefinitionService.boolean.yesNoSchema(),
          benefitsPaidAsCouple: fieldDefinitionService.boolean.yesNoSchema({
            title: "If you receive JSA/ESA, is your partner included in your claim?"
          }),
          benefits: {
            type: "object",
            properties: {
              attendanceAllowance: fieldDefinitionService.number.mappedCurrencyField({
                  title: 'Weekly Attendance Allowance / DLA Care / PIP Daily Living',
                  'enum': [
                    'high', 'medium', 'low', 'none'
                  ]
                }, {
                  type: 'select',
                  titleMap: [
                    toTitleMapEntry("high", attendanceAllowances.high.amount),
                    toTitleMapEntry("medium", attendanceAllowances.medium.amount),
                    toTitleMapEntry("low", attendanceAllowances.low.amount),
                    {value: 'none', name: '0'}
                  ]
                }
              ),
              mobilityAllowance: fieldDefinitionService.number.mappedCurrencyField({
                  title: 'Weekly DLA Mobility / PIP Mobility',
                  'enum': [
                    'high', 'low', 'none'
                  ]
                }, {
                  type: 'select',
                  titleMap: [
                    toTitleMapEntry("high", mobilityAllowances.high.amount),
                    toTitleMapEntry("low", mobilityAllowances.low.amount),
                    {value: 'none', name: '0'}
                  ]
                }
              ),

              incapacityBenefit: fieldDefinitionService.number.currencyField({
                title: 'JSA / ESA / Incapacity Benefit / Income Support'
              }),
              incapacityBenefitPeriod: customFrequency(["Weekly", "Fortnightly", "4 Weekly", "Annually"]),

              workingTaxCredit: fieldDefinitionService.number.currencyField({
                title: 'Total Household Working Tax Credit'
              }),
              workingTaxCreditPeriod: customFrequency(["Weekly", "4 Weekly"]),

              childTaxCredit: fieldDefinitionService.number.currencyField({
                title: 'Total Household Child Tax Credit'
              }),
              childTaxCreditPeriod: customFrequency(["Weekly", "4 Weekly"]),

              pensionGuaranteeCredit: fieldDefinitionService.number.currencyField({
                title: 'Total Household Pension Credit (Guarantee Credit)'
              }),
              pensionGuaranteeCreditPeriod: customFrequency(["Weekly", "Fortnightly", "4 Weekly", "Annually"]),

              pensionSavingsCredit: fieldDefinitionService.number.currencyField({
                title: 'Total Household Pension Credit (Savings Credit)'
              }),
              pensionSavingsCreditPeriod: customFrequency(["Weekly", "Fortnightly", "4 Weekly", "Annually"]),

              retirementPension: fieldDefinitionService.number.currencyField({
                title: 'State Retirement Pension'
              }),
              retirementPensionPeriod: customFrequency(["Weekly", "Fortnightly", "4 Weekly", "Annually"]),

              warPension: fieldDefinitionService.number.currencyField({
                title: 'War Widow(er)’s Pension'
              }),
              warPensionPeriod: customFrequency(["Weekly", "Fortnightly", "4 Weekly", "Annually"]),

              warDisablementPension: fieldDefinitionService.number.currencyField({
                title: "War Pension/War Disablement Pension"
              }),
              warDisablementPensionPeriod: customFrequency(["Weekly", "Fortnightly", "4 Weekly", "Annually"]),

              warSpecialPayment: fieldDefinitionService.number.currencyField({
                title: 'War Widow(er)’s Special Payment'
              }),
              warSpecialPaymentPeriod: frequency(),

              carersAllowance: fieldDefinitionService.number.currencyField({
                title: "Carer's Allowance"
              }),
              carersAllowancePeriod: customFrequency(["Weekly", "4 Weekly"]),

              childBenefit: fieldDefinitionService.number.currencyField({
                title: 'Child Benefit'
              }),
              childBenefitPeriod: customFrequency(["Weekly", "4 Weekly"]),

              otherBenefit: fieldDefinitionService.number.currencyField(
                {title: 'Any Other Income'},
                {description: 'Any other income or benefits you have not already told us about.'}
              ),
              otherBenefitPeriod: frequency()
            },

            required: [
              'incapacityBenefitPeriod',
              'pensionGuaranteeCreditPeriod',
              'workingTaxCreditPeriod',
              'childTaxCreditPeriod',
              'pensionSavingsCreditPeriod',
              'retirementPensionPeriod',
              'warPensionPeriod',
              'warDisablementPensionPeriod',
              'carersAllowancePeriod',
              'childBenefitPeriod',
              'otherBenefitPeriod'
            ]
          }
        },

        required: [
          'hasUniversalCredit',
          'universalCredit',
          'hasWage',
          'hasPrivatePension',
          'isEntitledToCarersPremium',
          'benefitsPaidAsCouple'
        ]
      };
    }

    function capital() {
      return {
        type: 'object',
        title: 'Capital',
        properties: {
          hasSavingsInvestments: fieldDefinitionService.boolean.yesNoSchema(),
          savingsInvestments: {
            type: 'array',
            minItems: 1,
            items: {
              type: "object",
              properties: {
                name: {
                  title: 'Name of Bank/BS Shares/Bonds',
                  type: 'string',
                  'x-schema-form': {
                    feedback: false
                  }
                },
                accountNo: {
                  title: 'Account No.',
                  type: 'string',
                  'x-schema-form': {
                    feedback: false
                  }
                },
                accountHolder: {
                  title: 'Account holder name(s)',
                  type: 'string',
                  'x-schema-form': {
                    feedback: false
                  }
                },
                amount: fieldDefinitionService.number.currencyField(
                  {title: 'Amount'},
                  {feedback: false}
                )
              },
              required: ["name", "accountNo", "accountHolder", "amount"]
            }
          },

          hasShareholdings: fieldDefinitionService.boolean.yesNoSchema(),
          shareholdings: {
            type: 'array',
            minItems: 1,
            items: {
              type: "object",
              properties: {
                companyName: {
                  title: 'Name of company',
                  type: 'string',
                  'x-schema-form': {
                    feedback: false
                  }
                },
                shareCount: {
                  title: 'Number of shares held',
                  type: 'integer',
                  'x-schema-form': {
                    feedback: false
                  }
                },
                totalValue: fieldDefinitionService.number.currencyField(
                  {title: 'Total value of shares'},
                  {feedback: false}
                )
              },
              required: ["companyName", "shareCount", "totalValue"]
            }
          }

        },
        required: ['hasSavingsInvestments', 'hasShareholdings']
      };
    }

    var schema = {
      type: 'object',
      properties: {
        clientReference: {
          title: "Client Reference",
          type: 'string'
        },
        person: {
          type: 'object',
          title: 'About you',
          properties: {
            details: {
              type: 'object',
              title: 'Details',
              properties: {
                title: {
                  title: 'Title',
                  type: 'string'
                },
                firstName: {
                  title: 'First name(s)',
                  type: 'string'
                },
                lastName: {
                  title: 'Surname',
                  type: 'string'
                },
                dob: fieldDefinitionService.time.dateOfBirth(),
                email: fieldDefinitionService.string.email({
                  title: 'Email address'
                }),
                nationalInsuranceNumber: fieldDefinitionService.NINO.fullNumber({
                  title: 'National insurance number'
                }),
                address: address(),
                phoneNumber: fieldDefinitionService.phone.localNumber({
                  title: 'Phone number'
                }),
                hasPartner: fieldDefinitionService.boolean.yesNoSchema({
                  title: 'Do you have a partner?'
                }),
                declarePartnerFinances: fieldDefinitionService.boolean.yesNoSchema({
                  title: 'Do you want to declare your partner’s finances?'
                }),
                propertyTenancy: {
                  title: 'Property/tenancy details',
                  type: 'string',
                  'enum': ['ownedByYou', 'ownedByPartner', 'ownedJointly', 'councilTenant', 'rentedPrivately', 'other'],
                  'x-schema-form': {
                    titleMap: titleMap({
                      ownedByYou: 'Property Owned By You',
                      ownedByPartner: 'Property Owned By Partner',
                      ownedJointly: 'Property Jointly Owned',
                      councilTenant: 'Council or Housing Association Tenant',
                      rentedPrivately: 'Property Rented From A Private Landlord',
                      other: 'Other (give details below)'
                    })
                  }
                },
                propertyTenancyAdditionalDetails: {
                  title: 'Additional property/tenancy details',
                  type: 'string',
                  'x-schema-form': {
                    type: "textarea"
                  }
                }
              },
              required: [
                'dob',
                'firstName',
                'lastName',
                'nationalInsuranceNumber',
                'address',
                'phoneNumber',
                'hasPartner',
                'declarePartnerFinances',
                'propertyTenancy',
                'propertyTenancyAdditionalDetails'
              ]
            },

            income: income(),
            capital: capital(),
            personalInjury: {
              type: 'object',
              properties: {
                hasReceivedCompensation: fieldDefinitionService.boolean.yesNoSchema()
              },
              required: ['hasReceivedCompensation']
            },
            healthInsurance: {
              type: 'object',
              properties: {
                hasInsurance: fieldDefinitionService.boolean.yesNoSchema(),
                details: {
                  type: "object",
                  title: 'Private Health Insurance/Hospital Fund Scheme',
                  properties: {
                    insurerName: {
                      title: 'Name of Insurer',
                      type: 'string'
                    },
                    insurerAddress: {
                      title: 'Address',
                      type: 'string',
                      'x-schema-form': {
                        type: 'textarea'
                      }
                    },
                    postcode: addressField.postcode({
                      'x-schema-form': {
                        title: 'Postcode'
                      }
                    }),
                    referenceNumber: {
                      title: 'Policy Reference No.',
                      type: 'string'
                    },
                    servicesCovered: {
                      title: 'Service Covered',
                      type: 'array',
                      items: {
                        type: 'string',
                        'enum': ['home', 'residential']
                      },
                      'x-schema-form': {
                        titleMap: titleMap({
                          home: 'Home Care',
                          residential: 'Residential Care'
                        })
                      }
                    }
                  },
                  required: ['insurerName', 'insurerAddress', 'servicesCovered']
                }
              },
              required: ['hasInsurance', 'details']
            },
            property: {
              type: 'object',
              title: 'Additional Property',
              properties: {
                hasAdditionalProperties: fieldDefinitionService.boolean.yesNoSchema({
                  title: 'Other than your home, do you solely or jointly own any properties or land?'
                }),
                additionalProperties: {
                  type: 'array',
                  minItems: 1,
                  items: {
                    type: "object",
                    properties: {
                      address: {
                        title: 'Address',
                        type: 'string',
                        'x-schema-form': {
                          type: 'textarea'
                        }
                      },
                      type: {
                        title: 'Type of Property (House, land, etc)',
                        type: 'string',
                        description: 'For example; houses, land, static caravans, property abroad'
                      },
                      isJointlyOwned: fieldDefinitionService.boolean.yesNoSchema({
                        title: 'Is it jointly owned?'
                      }),
                      value: fieldDefinitionService.number.currencyField({
                        title: 'Estimated Value'
                      }),
                      equity: fieldDefinitionService.number.currencyField({
                        title: 'What is the estimated value of your equity in the property?',
                        description: 'Equity is the difference between the value of the property and the amount still owed on a mortgage for the property. If the property is jointly owned only enter the value of your share of the equity.'
                      }),
                      isRented: fieldDefinitionService.boolean.yesNoSchema({
                        title: 'Do you receive rent from this property?'
                      })
                    },
                    required: ["address", "type", "isJointlyOwned", "value", "isRented", "equity"]
                  }
                }

              },

              required: ['hasAdditionalProperties']
            },
            expenses: {
              type: 'object',
              title: 'Expenses',
              properties: {

                rent: fieldDefinitionService.number.currencyField(
                  {title: 'Rent (after housing benefit / Universal Credit housing element)'},
                  {description: "Enter the total amount of rent, as shown on your tenancy agreement or most recent rent letter, minus any Housing Benefit or Universal Credit housing element you receive."}
                ),
                rentPeriod: frequency(),

                mortgage: fieldDefinitionService.number.currencyField({
                  title: 'Mortgage'
                }),
                mortgagePeriod: frequency(),

                councilTax: fieldDefinitionService.number.currencyField({
                  title: 'Annual Council Tax'
                }),

                waterRates: fieldDefinitionService.number.currencyField({
                  title: 'Water Rates'
                }),
                waterRatesPeriod: frequency(),

                homeContentsInsurance: fieldDefinitionService.number.currencyField({
                  title: 'Home Contents Insurance'
                }),
                homeContentsInsurancePeriod: frequency(),

                groundRent: fieldDefinitionService.number.currencyField({
                  title: 'Ground Rent'
                }),
                groundRentPeriod: frequency(),

                compulsoryPayments: fieldDefinitionService.number.currencyField(
                  {title: 'Court Ordered Payments'},
                  {description: 'Enter the total amount of any court ordered payments made (for example, Maintenance or Child Support)'}
                ),
                compulsoryPaymentsPeriod: frequency()

              },
              required: [
                'rentPeriod',
                'mortgagePeriod',
                'waterRatesPeriod',
                'homeContentsInsurancePeriod',
                'groundRentPeriod',
                'compulsoryPaymentsPeriod'
              ]
            }
          },
          required: ['details', 'capital', 'healthInsurance', 'income', 'property', 'expenses']
        },

        partner: {
          type: 'object',
          title: 'Your partner',
          properties: {

            details: {
              type: 'object',
              title: 'Details',
              properties: {
                title: {
                  title: 'Title',
                  type: 'string'
                },
                firstName: {
                  title: 'First name(s)',
                  type: 'string'
                },
                lastName: {
                  title: 'Surname',
                  type: 'string'
                },
                nationalInsuranceNumber: fieldDefinitionService.NINO.fullNumber({
                  title: 'National insurance number'
                }),
                dob: fieldDefinitionService.time.dateOfBirth()
              },
              required: ['firstName', 'lastName', 'dob', 'nationalInsuranceNumber']
            },


            income: income({
              salaryAmount: "This is the amount your partner receives after deductions and taxes have been taken off. Your partner will find this figure on their wage slip or pay cheque. If your partner is paid directly into their bank account, this figure will be shown on their bank statement.",
              pensionAmount: "This is the amount your partner receives after deductions and taxes have been taken off. Your partner will find this figure on their pension statement or cheque. If your partner's pension is paid directly into their bank account, this figure will be shown on their bank statement."
            }),
            capital: capital(),
            expenses: {
              type: 'object',
              title: 'Expenses',
              properties: {
                compulsoryPayments: fieldDefinitionService.number.currencyField(
                  {title: 'Court ordered payments'},
                  {description: 'Enter the total amount of any court ordered payments made (for example, Maintenance or Child Support)'}
                ),
                compulsoryPaymentsPeriod: frequency()
              },
              required: ['compulsoryPaymentsPeriod']
            }

          },
          required: ['details', 'capital', 'income']
        },

        joint: {
          type: 'object',
          properties: {
            capital: capital()
          },
          required: ['capital']
        },

        declaration: {
          type: 'object',
          properties: {
            provideFinancialDetails: {
              title: 'If you would like the Council to assist with the cost of your care you must fully declare your financial circumstances',
              description: htmlService.interpolateTemplate('modules/forms/scripts/services/forms/FCA/templates/financialDetailAdvice.html'),

              // Ideally, this fields would be a boolean, but due to this ASF bug we can't have multiple radio buttons
              // with the same value (false) https://github.com/json-schema-form/angular-schema-form/issues/887
              type: 'string',
              enum: ['agree', 'payFullCost', 'arrangeMyOwn'],
              'x-schema-form': {
                type: "radios",
                titleMap: titleMap({
                  agree: "I agree to provide my financial details to check whether I may be entitled to help with the cost of my care.",
                  payFullCost: "I have savings or capital above £23,250 in my sole name OR I do not wish to disclose my finances, but agree for Kirklees Council to arrange care services for me. I understand this means I have to pay the full cost of my care, and that this might incur an additional charge of £250 per year for the Council to arrange the care on my behalf.",
                  arrangeMyOwn: "I do not wish to provide my financial details and will arrange my own care."
                })
              }
            },
            isAccepted: fieldDefinitionService.boolean.yesRequiredSchema({
              title: 'I agree to and understand the above declaration'
            })
          },
          required: ['provideFinancialDetails', 'isAccepted']
        },

        agent: {
          type: 'object',
          properties: {
            isCompletedByAgent: fieldDefinitionService.boolean.yesNoSchema({
              title: 'Has this form been filled in by someone other than the person applying?'
            }),
            reason: fieldDefinitionService.default('Why was the form filled by someone else?'),

            details: {
              type: 'object',
              properties: {
                firstName: fieldDefinitionService.default('First name'),
                surname: fieldDefinitionService.default('Surname'),

                addressLine1: fieldDefinitionService.address.requiredLine({title: 'Address line 1'}, true),
                addressLine2: fieldDefinitionService.address.requiredLine({title: 'Address line 2'}, true),
                addressLine3: fieldDefinitionService.address.optionalLine({title: 'Address line 3'}, true),
                postcode: fieldDefinitionService.address.postcode({title: 'Postcode'}),

                phoneNumber: fieldDefinitionService.phone.fullNumber({
                  title: 'Daytime telephone number'
                }),
                relationship: {
                  title: 'Relationship to the person applying',
                  type: 'string',
                  'enum': ['advocate', 'friend', 'appointeeship', 'powerOfAttorney', 'lastingPowerOfAttorney', 'deputyship'],
                  'x-schema-form': {
                    titleMap: titleMap({
                      advocate: 'Advocate',
                      friend: 'Friend/Family member/neighbour',
                      appointeeship: 'I hold Appointeeship',
                      powerOfAttorney: 'I hold Power of Attorney',
                      lastingPowerOfAttorney: 'I hold Lasting Power of Attorney',
                      deputyship: 'I hold Deputyship - Court of Protection'
                    }),
                    description: htmlService.interpolateTemplate('modules/forms/scripts/services/forms/FCA/templates/relationshipDescription.html')
                  }
                }
              },
              required: ['firstName', 'surname', 'addressLine1', 'addressLine2', 'postcode', 'relationship']
            },

            applicantConfirmationDeclaration: fieldDefinitionService.boolean.yesRequiredSchema({
              title: 'I declare that as far as possible, I have confirmed with the person applying that the answers I have written on this form are correct',
              validationMessage: 'You must agree to the above declaration'
            }),
            agentDeclaration: fieldDefinitionService.boolean.yesRequiredSchema({
              title: 'I declare that the information I have provided in this form is correct and complete as far as I know and believe',
              validationMessage: 'You must agree to the above declaration'
            })
          },
          required: ['isCompletedByAgent', 'reason', 'details', 'applicantConfirmationDeclaration', 'agentDeclaration']
        },

        peopleLivingWithYou: {
          type: 'object',
          properties: {
            hasDependentChildren: fieldDefinitionService.boolean.yesNoSchema({
              title: 'Do you have dependent children that live with you?'
            }),
            dependentChildren: {
              type: 'array',
              minItems: 1,
              items: {
                type: "object",
                properties: {
                  name: {
                    title: 'Name',
                    type: 'string'
                  },
                  dateOfBirth: fieldDefinitionService.time.dateOfBirth({
                    'x-schema-form': {
                      validationMessage: 'A dependent child must be under 20 years old',
                      type: 'memorabledate',
                      dateFormat: "DD/MM/YYYY",
                      maxDate: new Date(),
                      minDate: dateUtils.getYearsBeforeNow(20)
                    }
                  })
                },
                required: ["name", "dateOfBirth"]
              }
            },
            hasOtherPeople: fieldDefinitionService.boolean.yesNoSchema({
              title: 'Do you have other people living with you?'
            }),
            otherPeople: {
              type: 'array',
              minItems: 1,
              items: {
                type: "object",
                properties: {
                  name: {
                    title: 'Name',
                    type: 'string'
                  },
                  dateOfBirth: fieldDefinitionService.time.dateOfBirth(),
                  relationship: {
                    title: 'Relationship, if any (son, daughter, lodger, etc.)',
                    type: 'string'
                  }
                },
                required: ["name"]
              }
            }
          },
          required: ['hasDependentChildren', 'hasOtherPeople']
        },

        longTermResidents: {
          type: "object",
          title: "Long Term Residents",
          properties: {
            deprivationOfAssets: {
              type: "object",
              properties: {
                hasDisposedOfAssets: fieldDefinitionService.boolean.yesNoSchema({
                  title: 'Have you or your partner sold, given away, gifted or put into trust any capital or assets in the last 5 years?'
                }),
                disposedAssets: {
                  type: 'array',
                  minItems: 1,
                  items: {
                    type: "object",
                    properties: {
                      date: fieldDefinitionService.time.pastDateSchema({
                        title: 'Date'
                      }),
                      description: {
                        title: 'Description',
                        type: 'string'
                      },
                      giftInformation: {
                        type: 'string',
                        'x-schema-form': {
                          type: 'template',
                          htmlClass: 'divTitle',
                          templateUrl: 'modules/forms/scripts/services/forms/FCA/templates/cfa_animation_12_capital_transfers-gifting-description-reason.html'
                        }
                      },
                      recipient: {
                        title: 'Recipient (name, address, relationship)',
                        type: 'string'
                      },
                      value: fieldDefinitionService.number.currencyField({
                        title: 'Estimated Value (£)'
                      })
                    },
                    required: ["date", "description", "recipient", "value"]
                  }
                },
                advisor: {
                  type: 'object',
                  properties: {
                    consent: fieldDefinitionService.boolean.yesNoSchema({
                      title: 'You may contact my solicitor or other professional advisor directly to obtain any relevant information or documents confirming the above, e.g. Deed of Gift',
                      description: 'If you deny us permission to contact your solicitor, you will need to provide any relevant information or documents confirming the above.'
                    }),
                    name: {
                      title: 'Name and address of Solicitor or Advisor',
                      type: 'string'
                    }
                  },
                  required: ['consent']
                }
              },
              required: ['hasDisposedOfAssets', 'disposedAssets', 'advisor']
            }
          },
          required: ['deprivationOfAssets']
        },

        letterOfAuthority: {
          type: 'object',
          properties: {
            isPersonNominated: fieldDefinitionService.boolean.yesNoSchema({
              title: 'Do you want to give permission for someone else to discuss your application and assessment on your behalf?'
            }),
            nominatedPersonDetails: {
              type: 'object',
              properties: {
                name: fieldDefinitionService.default('Name'),
                addressLine1: fieldDefinitionService.address.requiredLine({title: 'Address line 1'}, true),
                addressLine2: fieldDefinitionService.address.requiredLine({title: 'Address line 2'}, true),
                addressLine3: fieldDefinitionService.address.optionalLine({title: 'Address line 3'}, true),
                postcode: fieldDefinitionService.address.postcode({title: 'Postcode'}),

                phoneNumber: fieldDefinitionService.phone.fullNumber({
                  title: 'Daytime telephone number'
                }),

                emailAddress: fieldDefinitionService.string.email({
                  title: 'Email address'
                }),

                relationship: {
                  title: 'Relationship to the person applying',
                  type: 'string',
                  'enum': ['advocate', 'friend', 'appointeeship', 'powerOfAttorney', 'lastingPowerOfAttorney', 'deputyship'],
                  'x-schema-form': {
                    titleMap: titleMap({
                      advocate: 'Advocate',
                      friend: 'Friend/Family member/neighbour',
                      appointeeship: 'I hold Appointeeship',
                      powerOfAttorney: 'I hold Power of Attorney',
                      lastingPowerOfAttorney: 'I hold Lasting Power of Attorney',
                      deputyship: 'I hold Deputyship - Court of Protection'
                    })
                  }
                }
              },
              required: ['name', 'addressLine1', 'addressLine2', 'postcode', 'relationship']
            },
            permitDiscussion: fieldDefinitionService.boolean.yesRequiredSchema({
              title: 'I give permission for Kirklees to discuss my application and assessment with the nominated person',
              validationMessage: 'You must agree to the above declaration'
            }),
            permitCorrespondence: fieldDefinitionService.boolean.yesNoSchema({
              title: 'Do you want us to send correspondence relating to your application and assessment to the nominated person? If you select this option, the nominated person will receive correspondence on your behalf'
            })
          },
          required: ['isPersonNominated', 'nominatedPersonDetails', 'permitDiscussion', 'permitCorrespondence']
        },

        careAssessment: {
          type: 'object',
          properties: {
            careDecision: {
              type: "string",
              'enum': ["continue", "continueWithoutFinancialAssistance", "cancel"]
            },
            costOfCareFilter: {
              title: 'Which type of care do you want to see results for?',
              type: 'string',
              'enum': ['all', 'nonResidential', 'residential'],
              'x-schema-form': {
                titleMap: titleMap({
                  all: 'All Care Types',
                  nonResidential: 'Non-residential and Respite',
                  residential: 'Residential'
                })
              }
            },
            nonResidential: kirkleesCareAssessmentService.careTypeSchema(),
            respite: kirkleesCareAssessmentService.careTypeSchema(careHomeWorkingFieldOverrides()),
            week1Residential: kirkleesCareAssessmentService.careTypeSchema(careHomeWorkingFieldOverrides()),
            week5Residential: kirkleesCareAssessmentService.careTypeSchema(careHomeWorkingFieldOverrides()),
            week13Residential: kirkleesCareAssessmentService.careTypeSchema(careHomeWorkingFieldOverrides())
          },
          required: [
            'costOfCareFilter',
            'careDecision',
            'nonResidential',
            'respite',
            'week1Residential',
            'week5Residential',
            'week13Residential'
          ]
        }
      },

      required: [
        'letterOfAuthority',
        'agent',
        'careAssessment',
        'declaration',
        'joint',
        'longTermResidents',
        'peopleLivingWithYou',
        'person',
        'partner'
      ]
    };

    return {
      getSchema: function () {
        return schema;
      }
    };
  }]);
